import React from 'react';
import ReactDOM from 'react-dom';
import Dropdown from "../Dropdown/Dropdown";
import AddFirmModal from "../FirmList/AddFirmModal";
import { firmAddress } from "../FirmList/FirmModal";
import "./SelectFirm.scss";
import PropTypes from "prop-types";
import filter from "../../filter";
import { executeBackendQuery } from '../../requests/executeBackendQuery';
import { getData } from '../../requests/CRUD';
import { FIRMS_LIST, FIRMS_LIST_COUNT } from '../../consts/queries';
import { FIRMS } from '../../consts/tables';

const cloneDeep = require('lodash.clonedeep');
const limit = 10;
const debounceMs = 250;

/** The row under the matches that opens the /firms add popup. */
const ADD_NEW_OPTION = { addNewFirm: true, NAME: 'Add new firm' };

class SelectFirm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            items: [],
            hasChildValue: false,
            currentOffset: 0,
            hasMore: true,
            isLoading: false,
            searchTerm: "",
            inputValue: !props.value && props.firmName ? props.firmName : "",
            selectedOptions: [],
            addFirmOpen: false,
            addFirmName: "",
        };

        this.debounceTimer = null;
        this.activeRequestId = 0;
        this.typed = "";

        this.fetchPage = this.fetchPage.bind(this);
        this.startSearch = this.startSearch.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleSelectedOptionsChange = this.handleSelectedOptionsChange.bind(this);
        this.syncSelectedOptionsFromValue = this.syncSelectedOptionsFromValue.bind(this);
        this.ensureSelectedOption = this.ensureSelectedOption.bind(this);
        this.getOptionLabel = this.getOptionLabel.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleFirmAdded = this.handleFirmAdded.bind(this);
        this.renderOption = this.renderOption.bind(this);
    }

    componentDidMount() {
        this.startSearch("");
        this.ensureSelectedOption(this.props.value);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.newFirm !== prevProps.newFirm && this.props.newFirm) {
            const items = cloneDeep(this.state.items);
            items.push(this.props.newFirm);
            this.setState({ items }, this.syncSelectedOptionsFromValue);
        }

        if (prevProps.value !== this.props.value) {
            this.ensureSelectedOption(this.props.value);
        }

        if (!this.props.value && prevProps.firmName !== this.props.firmName) {
            this.setState({ inputValue: this.props.firmName || "" });
        }

        if (prevProps.value !== this.props.value || prevState.items !== this.state.items) {
            this.syncSelectedOptionsFromValue();
        }
    }

    componentWillUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        this.activeRequestId++;
    }

    extractId(option) {
        if (!option) return null;
        if (typeof option === 'object') {
            if (option.ARCIMEDES_ROW_ID !== undefined) return String(option.ARCIMEDES_ROW_ID);
            if (option.value !== undefined) return String(option.value);
        }
        return option !== undefined && option !== null ? String(option) : null;
    }

    getSelectedIdsFromValue() {
        const { value } = this.props;
        if (value === null || value === undefined) return [];
        if (Array.isArray(value)) return value.map((v) => String(this.extractId(v))).filter(Boolean);
        if (typeof value === 'object') {
            const id = this.extractId(value);
            return id ? [id] : [];
        }
        return String(value).split(',').map(v => v.trim()).filter(Boolean);
    }

    syncSelectedOptionsFromValue() {
        const selectedIds = this.getSelectedIdsFromValue();
        if (!selectedIds.length) {
            if (this.state.selectedOptions.length) {
                this.setState({ selectedOptions: [] });
            }
            return;
        }

        const map = new Map();
        this.state.selectedOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) map.set(id, opt);
        });
        this.state.items.forEach(opt => {
            const id = this.extractId(opt);
            if (id && selectedIds.includes(id)) {
                map.set(id, opt);
            }
        });

        const next = selectedIds.map(id => map.get(id)).filter(Boolean);
        const nextState = { selectedOptions: next };
        if (!this.state.searchTerm && next.length === 1) {
            const label = this.getOptionLabel(next[0]);
            if (label && this.state.inputValue !== label) {
                nextState.inputValue = label;
            }
        }
        this.setState(nextState);
    }

    ensureSelectedOption(value) {
        const id = this.extractId(value);
        if (!id) return;

        const alreadyThere = this.state.items.find(item => this.extractId(item) === id);
        if (alreadyThere) return;

        getData({
            tableCode: FIRMS,
            options: {
                limit: { count: 1, offset: 0 },
                column: ['NAME', 'ARCIMEDES_ROW_ID', 'PARENT_FIRM', 'DELETED'],
                filter: [
                    {
                        code: 'ARCIMEDES_ROW_ID',
                        text: id,
                        type: 'equal',
                    }
                ],
                filterType: 'and',
            },
        })
            .then((res) => {
                if (res && res.success && Array.isArray(res.data) && res.data.length) {
                    this.setState((prev) => ({
                        items: [res.data[0], ...prev.items],
                    }), this.syncSelectedOptionsFromValue);
                }
            })
            .catch(() => {});
    }

    fetchPage({ offset, searchTerm, replace }) {
        const requestId = ++this.activeRequestId;
        const trimmedTerm = (searchTerm || "").trim();

        const queryParams = this.props.searchAddress ? { SEARCH: trimmedTerm } : { NAME: trimmedTerm };
        if (this.props.excludeChildren) {
            queryParams.PARENT_FIRM = '';
        }
        const options = filter(queryParams, "firms");
        options.param.limit = limit;
        options.param.offset = offset;

        this.setState({ isLoading: true });

        return Promise.all([
            executeBackendQuery({ queryName: FIRMS_LIST, options }),
            executeBackendQuery({ queryName: FIRMS_LIST_COUNT, options }),
        ])
            .then((responses) => {
                if (requestId !== this.activeRequestId) return;

                const listRes = responses && responses[0] ? responses[0] : null;
                const countRes = responses && responses[1] ? responses[1] : null;

                if (listRes && listRes.success && Array.isArray(listRes.data)) {
                    const newItems = listRes.data.filter(item => item && item.NAME);
                    const totalCount = countRes && countRes.success && countRes.data && countRes.data[0]
                        ? Number(countRes.data[0].TOTAL_COUNT)
                        : null;
                    this.setState((prev) => ({
                        items: replace ? newItems : [...prev.items, ...newItems],
                        currentOffset: offset + newItems.length,
                        hasMore: totalCount === null ? (newItems.length === limit) : (offset + newItems.length < totalCount),
                        isLoading: false,
                        searchTerm,
                    }));
                } else {
                    this.setState({ isLoading: false, hasMore: false });
                }
            })
            .catch((err) => {
                if (requestId !== this.activeRequestId) return;
                console.error("Error fetching firms:", err);
                this.setState({ isLoading: false });
            });
    }

    startSearch(searchTerm) {
        this.setState({
            items: [],
            currentOffset: 0,
            hasMore: true,
            searchTerm,
        });
        return this.fetchPage({ offset: 0, searchTerm, replace: true });
    }

    handleInputChange(event, newValue, reason) {
        if (reason === "reset") return;
        if (reason === "clear") {
            this.typed = "";
            this.setState({ inputValue: "" });
            this.startSearch("");
            return;
        }

        const next = (newValue || "").trimStart();
        this.typed = next;
        this.setState({ inputValue: next });

        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.startSearch(next);
        }, debounceMs);
    }

    handleLoadMore() {
        if (!this.state.isLoading && this.state.hasMore) {
            this.fetchPage({
                offset: this.state.currentOffset,
                searchTerm: this.state.searchTerm,
                replace: false,
            });
        }
    }

    getOptionLabel(option) {
        if (!option || typeof option !== 'object') return '';
        return option.NAME || '';
    }

    handleSelectedOptionsChange(options) {
        if (options && options.addNewFirm) return;

        this.typed = "";
        const nextOptions = Array.isArray(options) ? options.filter(Boolean) : (options ? [options] : []);
        const map = new Map();
        nextOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) map.set(id, opt);
        });
        const selectedOptions = Array.from(map.values());
        const nextState = { selectedOptions };
        if (selectedOptions.length === 1) {
            const label = this.getOptionLabel(selectedOptions[0]);
            if (label) {
                nextState.inputValue = label;
                nextState.searchTerm = "";
            }
        }
        if (selectedOptions.length === 0) {
            nextState.inputValue = "";
            nextState.searchTerm = "";
        }
        this.setState(nextState);
    }

    handleChange(...args) {
        const picked = args[5];

        if (picked && picked.addNewFirm) {
            this.setState({ addFirmOpen: true, addFirmName: this.typed.trim() });
            return;
        }

        this.props.handleParentChange(...args);
    }

    handleFirmAdded(firm) {
        const { valueKey, data } = this.props;

        this.typed = "";
        this.setState((prev) => ({
            addFirmOpen: false,
            items: [firm, ...prev.items],
            selectedOptions: [firm],
            inputValue: this.getOptionLabel(firm),
            searchTerm: "",
        }));

        this.props.handleParentChange(
            { ...(data || {}), [valueKey]: firm.ARCIMEDES_ROW_ID },
            'firm',
            firm.ARCIMEDES_ROW_ID,
            valueKey,
            undefined,
            firm,
        );
    }

    renderOption(option) {
        if (option.addNewFirm) {
            return <span className="SelectFirm_add-new">+ Add new firm</span>;
        }

        const address = this.props.searchAddress ? firmAddress(option) : '';

        return (
            <div style={{display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0}}>
                <span>{option.NAME}</span>
                {address && (
                    <span style={{fontSize: '0.8em', opacity: 0.65, fontWeight: 'normal'}}>{address}</span>
                )}
            </div>
        );
    }

    render() {
        const { items, hasMore, isLoading, inputValue } = this.state;
        const {
            theme,
            value,
            valueKey,
            firmName,
            label,
            disabled,
            data,
            isFilter,
            withAddNew,
            hasDrawer,
            handleParentChange,
            addNewItem,
            deleted,
            viewMode,
            searchAddress,
            canAddFirm,
            ...rest
        } = this.props;

        const mergedOptionsMap = new Map();
        this.state.selectedOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) mergedOptionsMap.set(id, opt);
        });
        items.forEach(opt => {
            const id = this.extractId(opt);
            if (id && !mergedOptionsMap.has(id)) mergedOptionsMap.set(id, opt);
        });
        const mergedOptions = Array.from(mergedOptionsMap.values());

        const optionsWithIndent = mergedOptions.map((item) => {
            if (item && item.PARENT_FIRM) {
                return { ...item, NAME: '\u00a0\u00a0– ' + item.NAME };
            }
            return item;
        });

        if (canAddFirm && !viewMode) optionsWithIndent.push(ADD_NEW_OPTION);

        return (
            <React.Fragment>
            <Dropdown theme={theme}
                optionsType='firm'
                optionKey='ARCIMEDES_ROW_ID'
                options={optionsWithIndent}
                value={value}
                valueKey={valueKey}
                firmName={firmName}
                label={label}
                disabled={disabled}
                data={data}
                isFilter={isFilter}
                withAddNew={withAddNew}
                hasDrawer={hasDrawer}
                handleParentChange={this.handleChange}
                addNewItem={addNewItem}
                deleted={deleted}
                viewMode={viewMode}
                disableClientFilter={true}
                onInputChange={this.handleInputChange}
                inputValue={inputValue}
                loading={isLoading}
                loadingText="Searching..."
                noOptionsText={inputValue ? "No matching firms" : "Type to search firms"}
                onSelectedOptionsChange={this.handleSelectedOptionsChange}
                renderOption={(searchAddress || canAddFirm) ? this.renderOption : undefined}
                ListboxProps={{
                    onScroll: (e) => {
                        const { target } = e;
                        if (target.scrollTop + target.clientHeight >= target.scrollHeight * 0.8) {
                            if (hasMore && !isLoading) {
                                this.handleLoadMore();
                            }
                        }
                    }
                }}
                {...rest}
            />
            {canAddFirm && ReactDOM.createPortal(
                <AddFirmModal
                    show={this.state.addFirmOpen}
                    initialName={this.state.addFirmName}
                    onClose={() => this.setState({ addFirmOpen: false })}
                    onCreated={this.handleFirmAdded}
                />,
                document.body,
            )}
            </React.Fragment>
        )
    }
}

SelectFirm.propTypes = {
    theme: PropTypes.oneOf(['default', 'white', 'dark']),
    value: PropTypes.string,
    valueKey: PropTypes.string.isRequired,
    firmName: PropTypes.string,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    data: PropTypes.object,
    withAddNew: PropTypes.bool,
    handleParentChange: PropTypes.func.isRequired,
    isFilter: PropTypes.bool,
    viewMode: PropTypes.bool,
    hasDrawer: PropTypes.bool,
    addNewItem: PropTypes.func,
    deleted: PropTypes.bool,
    isFromProperty: PropTypes.bool,
    /** One box over name and address, with each firm's address under its name. */
    searchAddress: PropTypes.bool,
    /** "Add new firm" under the matches, opening the /firms add popup. */
    canAddFirm: PropTypes.bool,
};

export default SelectFirm;

